import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,
  Divider,
  useTheme
} from "@material-ui/core";
import { Alert } from "@material-ui/lab";
import { toast } from "react-toastify";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

const CompanySettingsModal = ({ open, onClose, companyId, currentUsersCount = 0, initialSettings }) => {
  const theme = useTheme();
  const [saving, setSaving] = useState(false);
  const [maxUsers, setMaxUsers] = useState(3);
  const [maxConnections, setMaxConnections] = useState(3);
  const [isNew, setIsNew] = useState(true);
  const [expirationDate, setExpirationDate] = useState(null);

  useEffect(() => {
    if (open) {
      if (initialSettings) {
        setMaxUsers(initialSettings.maxUsers || 3);
        setMaxConnections(initialSettings.maxConnections || 3);
        setExpirationDate(initialSettings.expirationDate ? new Date(initialSettings.expirationDate) : null);
        setIsNew(false);
      } else {
        setMaxUsers(3);
        setMaxConnections(1);
        setExpirationDate(null);
        setIsNew(true);
      }
    }
  }, [open, initialSettings]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        companyId: companyId,
        maxUsers: Number(maxUsers),
        maxConnections: Number(maxConnections),
        expirationDate: expirationDate ? expirationDate.toISOString() : null
      };

      if (isNew) {
        await api.post("/company-settings", payload);
        toast.success("Configuración creada exitosamente");
      } else {
        await api.put("/company-settings/" + companyId, payload);
        toast.success("Configuración actualizada exitosamente");
      }
      onClose(true);
    } catch (err) {
      toastError(err);
    } finally {
      setSaving(false);
    }
  };

  const usersDiff = maxUsers - currentUsersCount;
  let alertSeverity = "info";
  let alertMessage = "Actualmente hay " + currentUsersCount + " usuarios registrados.";

  if (usersDiff < 0) {
    alertSeverity = "error";
    alertMessage = "¡Atención! Hay " + currentUsersCount + " usuarios activos. Estás estableciendo un límite inferior a la cantidad actual (" + maxUsers + "). Esto impedirá crear nuevos usuarios.";
  } else if (usersDiff === 0) {
    alertSeverity = "warning";
    alertMessage = "Límite alcanzado. Hay " + currentUsersCount + " usuarios y el límite es " + maxUsers + ". No se podrán crear más usuarios.";
  } else {
    alertSeverity = "success";
    alertMessage = "Configuración válida. Se podrán crear " + usersDiff + " usuarios más.";
  }

  const daysLeft = expirationDate
    ? Math.ceil((expirationDate - new Date()) / (1000 * 60 * 60 * 24))
    : null;

  return (
    <Dialog open={open} onClose={() => onClose(false)} maxWidth="sm" fullWidth>
      <DialogTitle>
        {"Configurar Compañía #" + companyId}
      </DialogTitle>
      
      <DialogContent dividers>
        <Box display="flex" flexDirection="column" gap={3}>
          
          <Alert severity={alertSeverity} variant="outlined">
            {alertMessage}
          </Alert>

          <div>
            <Typography variant="subtitle2" color="textSecondary" gutterBottom style={{ fontWeight: 600 }}>
              Límites de Usuarios y Accesos
            </Typography>
            <TextField
              label="Máximo de Usuarios Permitidos"
              type="number"
              variant="outlined"
              fullWidth
              margin="dense"
              value={maxUsers}
              onChange={(e) => setMaxUsers(e.target.value)}
              helperText="Define cuántas cuentas de usuario puede tener esta compañía."
              InputProps={{ inputProps: { min: 1 } }}
              error={usersDiff < 0}
            />
          </div>
          
          <Divider />

          <div>
            <Typography variant="subtitle2" color="textSecondary" gutterBottom style={{ fontWeight: 600 }}>
              Límites de Conectividad
            </Typography>
            <TextField
              label="Máximo de Conexiones WhatsApp"
              type="number"
              variant="outlined"
              fullWidth
              margin="dense"
              value={maxConnections}
              onChange={(e) => setMaxConnections(e.target.value)}
              helperText="Cantidad máxima de dispositivos WhatsApp o Twilio que pueden vincular simultáneamente."
              InputProps={{ inputProps: { min: 1 } }}
            />
          </div>

          <Divider />

          <div>
						<Box display="flex" alignItems="center">
							<DatePicker
								selected={expirationDate}
								onChange={(date) => setExpirationDate(date)}
								dateFormat="dd/MM/yyyy"
								minDate={new Date()}
								customInput={
									<TextField
										variant="outlined"
										margin="dense"
										placeholder="dd/mm/aaaa"
										style={{ flex: 1 }}
									/>
								}
								calendarClassName={theme.palette.type === "dark" ? "react-datepicker-dark" : "react-datepicker-light"}
							/>

							<Box ml={1} display="flex" gap={1}>
								<Button
									size="small"
									variant="outlined"
									color="primary"
									onClick={() => setExpirationDate(new Date(new Date().setMonth(new Date().getMonth() + 1)))}
								>
									+1 mes
								</Button>
								<Button
									size="small"
									variant="outlined"
									color="primary"
									onClick={() => setExpirationDate(new Date(new Date().setFullYear(new Date().getFullYear() + 1)))}
								>
									+1 año
								</Button>
							</Box>
						</Box>

						{expirationDate && (
							<Typography variant="caption" color="textSecondary" style={{ display: "block", marginTop: 4 }}>
								{daysLeft > 0 ? "Expira en " + daysLeft + " días" : "Expirada"}
							</Typography>
						)}
					</div>

        </Box>
      </DialogContent>
      
      <DialogActions>
        <Button onClick={() => onClose(false)} color="default" disabled={saving}>
          Cancelar
        </Button>
        <Button 
          onClick={handleSave} 
          color="primary" 
          variant="contained" 
          disabled={saving}
        >
          {saving ? "Guardando..." : "Guardar Cambios"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CompanySettingsModal;
